import React, { useState, useEffect } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Menu, X, LogOut, LayoutDashboard, MessageCircle, ExternalLink, ChevronDown, BookOpen, Sparkles } from "lucide-react";
import GlobalSearchBar from "@/components/GlobalSearchBar";
import api from "@/lib/api";
import { saveReturnUrl } from "@/lib/postLoginRedirect";
import { getCoursePricing, formatINR } from "@/lib/pricing";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

// Slim navbar — only essentials. Community / Challenges / Student Results moved to Footer.
const navItems = [
  { to: "/", label: "Home" },
  { to: "/webinars", label: "Webinars", dot: "emerald" },
  { to: "/live", label: "Live Batch", dot: "red", pulse: true },
  { to: "/#about", label: "About" },
  { to: "/#contact", label: "Contact" },
];

const DOT_COLORS = {
  emerald: "bg-emerald-400",
  red: "bg-red-500",
  amber: "bg-amber-400",
};

const WHATSAPP_URL = "https://wa.me/919131341638?text=Hi%20Kamlesh%20Sir%2C%20I%20want%20to%20know%20about%20your%20courses";

export default function Navbar() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [courses, setCourses] = useState([]);
  const [coursesOpen, setCoursesOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    api.get("/courses").then(({ data }) => setCourses(data || [])).catch(() => {});
  }, []);

  const dashboardPath = user?.role === "admin" ? "/dashboard/admin" : user?.role === "teacher" ? "/dashboard/teacher" : "/dashboard/student";

  // Persist the current pathname before sending users to /login so they
  // come back exactly where they were (works across Google OAuth too).
  const rememberCurrentPage = () => {
    const p = location.pathname + location.search;
    if (!p.startsWith("/login") && !p.startsWith("/register") && !p.startsWith("/auth/")) {
      saveReturnUrl(p);
    }
  };

  const onAnchor = (e, hash) => {
    if (location.pathname !== "/") return;
    e.preventDefault();
    const el = document.querySelector(hash);
    el?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <>
      <header className={`sticky top-0 z-50 transition-all ${scrolled ? "bg-[#050505]/90 backdrop-blur-md border-b border-white/10" : "bg-transparent"}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-2">
          <Link to="/" className="flex items-center gap-2.5 shrink-0" data-testid="navbar-logo">
            <img src="https://customer-assets.emergentagent.com/job_batch-academy-2/artifacts/fpz48xo5_512X512%20CWK%20logo%20%281%29.png" alt="CWK" className="w-9 h-9 rounded-lg object-contain" />
            <div className="hidden sm:block">
              <div className="font-display text-base font-bold text-white leading-none">CodeWithKamlesh</div>
              <div className="text-[10px] text-white/50 uppercase tracking-[0.18em] mt-0.5">Learn · Live · Lead</div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            <NavLink to="/" end className={({ isActive }) => `px-3 py-2 rounded-lg text-sm font-medium transition ${isActive ? "text-[#FF0066] bg-[#FF0066]/10" : "text-white/70 hover:text-white hover:bg-white/5"}`} data-testid="nav-home">Home</NavLink>

            {/* ============ COURSES DROPDOWN ============ */}
            <DropdownMenu open={coursesOpen} onOpenChange={setCoursesOpen}>
              <DropdownMenuTrigger asChild>
                <button
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition inline-flex items-center gap-1.5 ${location.pathname.startsWith("/courses") ? "text-[#FF0066] bg-[#FF0066]/10" : "text-white/70 hover:text-white hover:bg-white/5"}`}
                  data-testid="nav-courses-dropdown"
                >
                  Courses <ChevronDown className={`w-3.5 h-3.5 transition-transform ${coursesOpen ? "rotate-180" : ""}`} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-[420px] bg-[#0a0a0a]/95 backdrop-blur-md border-white/10 text-white p-2">
                <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-[#FF0066] font-bold flex items-center gap-1.5 px-2">
                  <Sparkles className="w-3 h-3" /> Our Programs
                </DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-white/10" />
                {courses.length === 0 ? (
                  <div className="text-xs text-white/40 px-3 py-3">Loading courses…</div>
                ) : (
                  courses.map((c) => {
                    const { final } = getCoursePricing(c);
                    return (
                      <DropdownMenuItem
                        key={c.id}
                        onClick={() => { navigate(`/courses/${c.slug || c.id}`); setCoursesOpen(false); }}
                        className="focus:bg-white/5 focus:text-white cursor-pointer p-3 rounded-lg"
                        data-testid={`nav-course-${c.id}`}
                      >
                        <div className="flex items-start gap-3 w-full">
                          {c.image_url ? (
                            <img src={c.image_url} alt={c.title} className="w-12 h-12 rounded-lg object-cover shrink-0 ring-1 ring-white/10" />
                          ) : (
                            <div className="w-12 h-12 rounded-lg bg-[#FF0066]/15 flex items-center justify-center shrink-0">
                              <BookOpen className="w-4 h-4 text-[#FF0066]" />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-white truncate">{c.title}</div>
                            <div className="text-[10px] text-white/50 mt-0.5 uppercase tracking-wider">{c.category}</div>
                            {final > 0 ? <div className="text-xs text-[#FF0066] font-bold mt-0.5" data-testid={`nav-price-${c.id}`}>{formatINR(final, { showFree: false })}</div> : null}
                          </div>
                        </div>
                      </DropdownMenuItem>
                    );
                  })
                )}
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuItem
                  onClick={() => { navigate("/courses"); setCoursesOpen(false); }}
                  className="focus:bg-white/5 focus:text-white cursor-pointer px-3 py-2 text-xs text-[#FF0066] font-semibold justify-center"
                  data-testid="nav-courses-view-all"
                >
                  View all courses →
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {navItems.slice(1).map((n) => n.to.includes("#") ? (
              <a key={n.label} href={n.to} onClick={(e) => onAnchor(e, n.to.replace("/", ""))}
                className="px-3 py-2 rounded-lg text-sm font-medium text-white/70 hover:text-white hover:bg-white/5 transition" data-testid={`nav-${n.label.toLowerCase().replace(/ /g, "-")}`}>
                {n.label}
              </a>
            ) : (
              <NavLink key={n.to} to={n.to} data-testid={`nav-${n.label.toLowerCase().replace(/ /g, "-")}`}
                className={({ isActive }) => `px-3 py-2 rounded-lg text-sm font-medium transition inline-flex items-center gap-1.5 ${isActive ? "text-[#FF0066] bg-[#FF0066]/10" : "text-white/70 hover:text-white hover:bg-white/5"}`}>
                {n.dot && (
                  <span className="relative flex h-2 w-2">
                    {n.pulse && <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${DOT_COLORS[n.dot]} opacity-70`} />}
                    <span className={`relative inline-flex rounded-full h-2 w-2 ${DOT_COLORS[n.dot]}`} />
                  </span>
                )}
                {n.label}
              </NavLink>
            ))}

            <a href="https://codewithkamlesh.in" target="_blank" rel="noreferrer"
              className="px-3 py-2 rounded-lg text-sm font-medium text-white/70 hover:text-[#FF0066] hover:bg-white/5 transition inline-flex items-center gap-1" data-testid="nav-main-website">
              Main Website <ExternalLink className="w-3 h-3" />
            </a>
          </nav>

          <div className="hidden md:flex items-center gap-2">
            {user && <GlobalSearchBar />}
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-white/15 hover:bg-white/5" data-testid="user-menu-btn">
                    <div className="w-7 h-7 rounded-full bg-[#FF0066] flex items-center justify-center text-xs font-bold text-white">
                      {user.name?.[0]?.toUpperCase() || "U"}
                    </div>
                    <span className="text-sm text-white/90">{user.name?.split(" ")[0]}</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 bg-[#121212] border-white/10 text-white">
                  <DropdownMenuLabel>
                    <div className="text-sm font-medium text-white">{user.name}</div>
                    <div className="text-xs text-white/60">{user.email}</div>
                    <div className="mt-1 inline-block text-[10px] uppercase tracking-wider bg-[#FF0066]/10 text-[#FF0066] px-2 py-0.5 rounded-full">{user.role}</div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator className="bg-white/10" />
                  <DropdownMenuItem onClick={() => navigate(dashboardPath)} data-testid="menu-dashboard" className="focus:bg-white/5 focus:text-white">
                    <LayoutDashboard className="w-4 h-4 mr-2" /> Dashboard
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/community")} className="focus:bg-white/5 focus:text-white">
                    💬 Community
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/challenges")} className="focus:bg-white/5 focus:text-white">
                    🔥 Challenges
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => { logout(); navigate("/"); }} data-testid="menu-logout" className="focus:bg-white/5 focus:text-white">
                    <LogOut className="w-4 h-4 mr-2" /> Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <Link to="/login" onClick={rememberCurrentPage} className="text-sm font-medium text-white/80 hover:text-white px-3 py-2" data-testid="nav-login">Login</Link>
                <Link to="/courses" className="inline-flex items-center justify-center bg-[#FF0066] hover:bg-[#e6005c] text-white text-sm font-medium rounded-full px-4 py-2 transition shadow-[0_4px_20px_-4px_rgba(255,0,102,0.5)]" data-testid="nav-enroll">Enroll Now</Link>
              </>
            )}
          </div>

          <button className="lg:hidden p-2 rounded-lg border border-white/15 hover:bg-white/5" onClick={() => setOpen(!open)} data-testid="mobile-menu-btn">
            {open ? <X className="w-5 h-5 text-white" /> : <Menu className="w-5 h-5 text-white" />}
          </button>
        </div>

        {open && (
          <div className="lg:hidden border-t border-white/10 bg-[#050505]">
            <div className="px-4 py-4 flex flex-col gap-1">
              <Link to="/" onClick={() => setOpen(false)} className="px-3 py-2.5 rounded-lg text-sm font-medium text-white/80 hover:bg-white/5">Home</Link>

              {/* Mobile Courses with sub-list */}
              <div className="px-3 py-2.5 text-xs uppercase tracking-wider text-[#FF0066] font-bold">Courses</div>
              <div className="pl-2 flex flex-col gap-1">
                {courses.map((c) => (
                  <Link key={c.id} to={`/courses/${c.slug || c.id}`} onClick={() => setOpen(false)} className="px-3 py-2 rounded-lg text-sm font-medium text-white/80 hover:bg-white/5 flex items-center gap-2" data-testid={`m-nav-course-${c.id}`}>
                    <BookOpen className="w-3.5 h-3.5 text-[#FF0066]" /> {c.title}
                  </Link>
                ))}
                <Link to="/courses" onClick={() => setOpen(false)} className="px-3 py-2 rounded-lg text-sm font-semibold text-[#FF0066] hover:bg-white/5">View all courses →</Link>
              </div>
              <div className="h-px bg-white/10 my-2" />

              {navItems.slice(1).map((n) => n.to.includes("#") ? (
                <a key={n.label} href={n.to} onClick={(e) => { onAnchor(e, n.to.replace("/", "")); setOpen(false); }} className="px-3 py-2.5 rounded-lg text-sm font-medium text-white/80 hover:bg-white/5">{n.label}</a>
              ) : (
                <Link key={n.to} to={n.to} onClick={() => setOpen(false)} className="px-3 py-2.5 rounded-lg text-sm font-medium text-white/80 hover:bg-white/5 inline-flex items-center gap-2">
                  {n.dot && (
                    <span className="relative flex h-2 w-2">
                      {n.pulse && <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${DOT_COLORS[n.dot]} opacity-70`} />}
                      <span className={`relative inline-flex rounded-full h-2 w-2 ${DOT_COLORS[n.dot]}`} />
                    </span>
                  )}
                  {n.label}
                </Link>
              ))}
              <a href="https://codewithkamlesh.in" target="_blank" rel="noreferrer" className="px-3 py-2.5 rounded-lg text-sm font-medium text-white/80 hover:bg-white/5 inline-flex items-center gap-1.5">Main Website <ExternalLink className="w-3.5 h-3.5" /></a>

              <div className="h-px bg-white/10 my-2" />
              {user ? (
                <>
                  <Link to={dashboardPath} onClick={() => setOpen(false)} className="px-3 py-2.5 rounded-lg text-sm font-medium text-white/80 hover:bg-white/5">Dashboard</Link>
                  <Link to="/community" onClick={() => setOpen(false)} className="px-3 py-2.5 rounded-lg text-sm font-medium text-white/80 hover:bg-white/5">💬 Community</Link>
                  <Link to="/challenges" onClick={() => setOpen(false)} className="px-3 py-2.5 rounded-lg text-sm font-medium text-white/80 hover:bg-white/5">🔥 Challenges</Link>
                  <button onClick={() => { logout(); setOpen(false); navigate("/"); }} className="text-left px-3 py-2.5 rounded-lg text-sm font-medium text-white/80 hover:bg-white/5">Logout</button>
                </>
              ) : (
                <>
                  <Link to="/login" onClick={() => { rememberCurrentPage(); setOpen(false); }} className="px-3 py-2.5 rounded-lg text-sm font-medium text-center border border-white/15 text-white">Login</Link>
                  <Link to="/courses" onClick={() => setOpen(false)} className="px-3 py-2.5 rounded-lg text-sm font-medium text-center bg-[#FF0066] text-white">Enroll Now</Link>
                </>
              )}
            </div>
          </div>
        )}
      </header>

      {/* Sticky mobile WhatsApp CTA */}
      {!/(dashboard|login|register|live\/)/.test(location.pathname) && (
        <a href={WHATSAPP_URL} target="_blank" rel="noreferrer"
          className="md:hidden fixed bottom-5 right-5 z-40 inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#128C7E] text-white font-medium text-sm rounded-full pl-3 pr-4 py-2.5 shadow-xl shadow-green-500/30 transition"
          data-testid="sticky-whatsapp-btn">
          <MessageCircle className="w-4 h-4" /> Talk on WhatsApp
        </a>
      )}
    </>
  );
}
